import { useSearchParams } from "react-router-dom";

import { type ChainListParams, useChains } from "@/api";
import { ChainCard } from "@/components/ChainCard";
import { FilterSidebar } from "@/components/FilterSidebar";

const PAGE_SIZE = 10;

function paramsFromSearch(search: URLSearchParams): ChainListParams {
  const numOrUndefined = (v: string | null) =>
    v != null && v !== "" ? Number(v) : undefined;
  return {
    k: numOrUndefined(search.get("k")),
    origin_market: search.get("origin_market") || undefined,
    destination_market: search.get("destination_market") || undefined,
    max_deadhead_mi: numOrUndefined(search.get("max_deadhead_mi")),
    min_slack_min: numOrUndefined(search.get("min_slack_min")),
    max_duration_days: numOrUndefined(search.get("max_duration_days")),
    risks: search.get("risks") || undefined,
    // Dates are passed straight through as ISO strings (`YYYY-MM-DD`) —
    // the backend's Pydantic schema parses them into `date`. We don't
    // validate format client-side; an empty string is treated as omitted.
    pickup_date: search.get("pickup_date") || undefined,
    chain_end_date: search.get("chain_end_date") || undefined,
    page: numOrUndefined(search.get("page")) ?? 1,
    page_size: PAGE_SIZE,
  };
}

function paramsToSearch(p: ChainListParams): URLSearchParams {
  const out = new URLSearchParams();
  for (const [k, v] of Object.entries(p)) {
    if (v === undefined || v === null || v === "") continue;
    if (k === "page_size") continue;
    out.set(k, String(v));
  }
  return out;
}

export function ChainsListPage(): JSX.Element {
  const [search, setSearch] = useSearchParams();
  const params = paramsFromSearch(search);
  const { data, isLoading, isError, error, isFetching } = useChains(params);

  const updateParams = (next: ChainListParams) => {
    // Reset to page 1 on filter changes.
    setSearch(paramsToSearch({ ...next, page: 1 }));
  };

  const goToPage = (page: number) => {
    setSearch(paramsToSearch({ ...params, page }));
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] gap-6">
      <FilterSidebar initial={params} onApply={updateParams} />

      <div className="space-y-3">
        <div className="flex items-baseline justify-between">
          <h1 className="text-xl font-semibold text-slate-900">Chains</h1>
          {isFetching && !isLoading && (
            <span className="text-xs text-slate-400">refreshing…</span>
          )}
        </div>

        {isLoading && (
          <div className="card p-6 text-center text-slate-500">Loading chains…</div>
        )}

        {isError && (
          <div className="card p-6 text-rose-600">
            Failed to load chains: {error instanceof Error ? error.message : "unknown error"}
          </div>
        )}

        {!isLoading && !isError && data && (
          <>
            <div className="text-sm text-slate-500">
              {data.total.toLocaleString()} {data.total === 1 ? "chain" : "chains"} match
            </div>
            {data.chains.length === 0 ? (
              <div className="card p-6 text-center text-slate-500">
                No chains match the current filters.
              </div>
            ) : (
              <ul className="space-y-2">
                {data.chains.map((c) => (
                  <li key={c.chain_id}>
                    <ChainCard chain={c} />
                  </li>
                ))}
              </ul>
            )}
            {data.total > PAGE_SIZE && (
              <Pagination
                page={data.page}
                pageSize={data.page_size}
                total={data.total}
                onPage={goToPage}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
}

function Pagination({ page, pageSize, total, onPage }: PaginationProps): JSX.Element {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="flex items-center justify-between pt-2">
      <button
        type="button"
        className="btn-secondary"
        disabled={page <= 1}
        onClick={() => onPage(page - 1)}
      >
        ← Previous
      </button>
      <span className="text-sm text-slate-600">
        page {page} of {totalPages}
      </span>
      <button
        type="button"
        className="btn-secondary"
        disabled={page >= totalPages}
        onClick={() => onPage(page + 1)}
      >
        Next →
      </button>
    </div>
  );
}
